import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Modal, Button, ConfirmModal, Flex } from 'nallian-shared-ui/lib/components';
import { ImpersonationRequestDetailView, ImpersonationStatus } from 'nallian-shared-ui/lib/impersonation';
import impersonationRequestDetailService from '../../../impersonationRequests/services/impersonationRequestDetailService';
import coApproveImpersonationService from '../../services/impersonation/coApproveImpersonationService';
import coDenyImpersonationService from '../../services/impersonation/coDenyImpersonationService';

const ImpersonationRequestDetailModal = ({
  open,
  rootUrl,
  dispatchImpersonationRequestDetailGet,
  impersonationRequestDetailFetch,
  dispatchCoApproveImpersonationPut,
  coApproveImpersonationFetch,
  dispatchCoDenyImpersonationPut,
  coDenyImpersonationFetch,
}) => {
  const { t } = useTranslation();
  const { actionId } = useParams();
  const navigate = useNavigate();

  const handleModalClose = useCallback(() => {
    navigate(rootUrl);
  }, [rootUrl, navigate]);

  useEffect(() => {
    if (open && actionId) {
      dispatchImpersonationRequestDetailGet(actionId);
    }
  }, [open, actionId, dispatchImpersonationRequestDetailGet]);

  const handleAccept = useCallback(() => {
    dispatchCoApproveImpersonationPut(actionId, {}, handleModalClose, t);
  }, [actionId, dispatchCoApproveImpersonationPut, handleModalClose, t]);

  const handleDeny = useCallback(() => {
    dispatchCoDenyImpersonationPut(actionId, {}, handleModalClose, t);
  }, [actionId, dispatchCoDenyImpersonationPut, handleModalClose, t]);

  if (!open) return null;

  const request = impersonationRequestDetailFetch?.value;
  const isProcessing =
    impersonationRequestDetailFetch?.pending ||
    coApproveImpersonationFetch?.pending ||
    coDenyImpersonationFetch?.pending;

  return (
    <Modal open closeIcon onClose={handleModalClose} size="small">
      <Modal.Header>
        <Flex justifyContent="space-between" alignItems="center">
          {t('impersonationRequestDetails')}
          <ImpersonationStatus value={request?.status} />
        </Flex>
      </Modal.Header>
      <Modal.Content>
        <ImpersonationRequestDetailView
          request={request}
          loading={isProcessing}
        />
      </Modal.Content>
      <Modal.Actions>
        <ConfirmModal
          header={t('impersonation.confirmDeny')}
          cancelContent={t('cancel')}
          confirmContent={t('deny')}
          confirmColor="red"
          onConfirm={handleDeny}
          trigger={
            <Button content={t('deny')} color="red" icon="cancel" disabled={isProcessing} />
          }
        />
        <ConfirmModal
          header={t('impersonation.confirmApprove')}
          cancelContent={t('cancel')}
          confirmContent={t('accept')}
          confirmColor="green"
          onConfirm={handleAccept}
          trigger={
            <Button content={t('accept')} color="green" icon="check" disabled={isProcessing} />
          }
        />
      </Modal.Actions>
    </Modal>
  );
};

ImpersonationRequestDetailModal.propTypes = {
  open: PropTypes.bool,
  rootUrl: PropTypes.string.isRequired,
  dispatchImpersonationRequestDetailGet: PropTypes.func.isRequired,
  impersonationRequestDetailFetch: PropTypes.object.isRequired,
  dispatchCoApproveImpersonationPut: PropTypes.func.isRequired,
  coApproveImpersonationFetch: PropTypes.object.isRequired,
  dispatchCoDenyImpersonationPut: PropTypes.func.isRequired,
  coDenyImpersonationFetch: PropTypes.object.isRequired,
};

export default compose(
  impersonationRequestDetailService,
  coApproveImpersonationService,
  coDenyImpersonationService
)(ImpersonationRequestDetailModal);
